'use client'

import { useEffect, useState } from 'react'
import { useRouter, useSearchParams } from '@/lib/next-shim'
import { Trans } from '@lingui/react/macro'
import { Link } from '@/lib/next-shim'

import { useGetPaymentStatusQuery } from '@/core/graphql/hooks'
import { useRequiredParams } from '@/hooks/useRequiredParams'
import { useGetOrder } from '@/shared/api/order'

import BookingConfirmationDialog from './BookingConfirmationDialog'
import { clearState } from '../lib/bookingStorage'

export function BookingPaymentSuccessPage() {
  const { meetingLink } = useRequiredParams(['meetingLink'])
  const router = useRouter()
  const searchParams = useSearchParams()
  const [verificationSuccess, setVerificationSuccess] = useState(false)
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)
  const [autoShowConfirmationDialog, setAutoShowConfirmationDialog] = useState(true)

  // Get parameters from different sources with fallbacks
  const orderId = searchParams?.get('orderId') || ''

  const { order, isLoadingOrder } = useGetOrder(orderId)

  // Use the GraphQL query to verify payment status directly
  const {
    data,
    loading: isLoadingPaymentStatus,
    error,
  } = useGetPaymentStatusQuery({
    variables: { payload: { orderId } },
    skip: !orderId,
    // Don't cache this query - we want fresh data
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    clearState()
  }, [])

  // Automatically show confirmation dialog when payment and order update are both successful
  useEffect(() => {
    if (autoShowConfirmationDialog && verificationSuccess && !showConfirmationDialog) {
      // Add a small delay for better UX
      const timer = setTimeout(() => {
        setAutoShowConfirmationDialog(false)
        setShowConfirmationDialog(true)
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [autoShowConfirmationDialog, verificationSuccess, showConfirmationDialog])

  useEffect(() => {
    if (!data) return

    // If the payment is verified as paid, set success state
    if (data.getPaymentStatus.paid) {
      setVerificationSuccess(true)

      // If it's an immediate consultation, auto-redirect to waiting room
      if (order?.consultation?._id) {
        console.log('🚀 [IMMEDIATE] Auto-redirecting to waiting room from payment success:', order.consultation._id)
        router.push(`/account/consultations/${order.consultation._id}/waiting`)
      }
      return
    }

    // If payment is not verified as paid, redirect to booking page
    router.push(`/meeting-links/${meetingLink}/booking`)
  }, [data, meetingLink, router, order])

  if (isLoadingOrder || isLoadingPaymentStatus) {
    return <VerifyPaymentLoader />
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-lg">
          <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h3 className="mb-1 text-lg font-semibold">
            <Trans>Missing order ID</Trans>
          </h3>
          <p className="text-gray-500 mb-6">
            <Trans>Could not verify payment</Trans>
          </p>
          <Link href="/account/dashboard" className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
            <Trans>Dashboard</Trans>
          </Link>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-lg">
          <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h3 className="mb-1 text-lg font-semibold">
            <Trans>Error during payment verification</Trans>
          </h3>
          <p className="text-gray-500 mb-6">
            <Trans>Payment verification error</Trans>
          </p>
          <Link
            href={meetingLink ? `/meeting-links/${meetingLink}/booking/payment` : '/'}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            <Trans>Return to booking</Trans>
          </Link>
        </div>
      </div>
    )
  }

  if (verificationSuccess) {
    return (
      <>
        <div className="flex-1 flex items-center justify-center px-5">
          <div className="flex flex-col items-center gap-8 max-w-lg">
            <div className="text-center">
              <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <svg className="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="mb-1 text-lg font-semibold">
                <Trans>Payment successful</Trans>
              </h3>
              <p className="text-gray-500">
                <Trans>Your payment has been processed successfully</Trans>
              </p>
            </div>
            <div className="w-full flex items-center justify-center gap-5">
              {data?.getPaymentStatus.receiptUrl && (
                <a
                  href={data.getPaymentStatus.receiptUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 leading-normal text-center"
                >
                  <Trans>View receipt</Trans>
                </a>
              )}
              <button
                onClick={() => setShowConfirmationDialog(true)}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-base leading-normal text-center"
              >
                <Trans>View booking details</Trans>
              </button>
            </div>
            <Link href="/account/dashboard" className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
              <Trans>Dashboard</Trans>
            </Link>
          </div>
        </div>
        <BookingConfirmationDialog open={showConfirmationDialog} onClose={() => setShowConfirmationDialog(false)} referenceId={order.referenceId} />
      </>
    )
  }

  // Fallback view
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center max-w-lg">
        <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100">
          <svg className="h-8 w-8 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h3 className="mb-1 text-lg font-semibold">
          <Trans>Payment status unknown</Trans>
        </h3>
        <p className="text-gray-500 mb-6">
          <Trans>Payment verification pending</Trans>
        </p>
        <Link href={meetingLink ? `/meeting-links/${meetingLink}/booking` : '/'} className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
          <Trans>Return to booking</Trans>
        </Link>
      </div>
    </div>
  )
}

function VerifyPaymentLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-400 border-t-white" />
        </div>
        <h3 className="mb-1 text-lg font-semibold">
          <Trans>Verifying payment...</Trans>
        </h3>
        <p className="text-gray-500">
          <Trans>Please wait</Trans>
        </p>
      </div>
    </div>
  )
}
